import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Modal, Typography, Divider } from '@mui/material';
import { IoMdClose } from 'react-icons/io';
import { Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../../../redux/hook';
import {
  employeeUpload,
  employeeAttendanceUpload,
  employeeLeaveCreditUpload,
  employeeLeaveAvailedUpload,
  employeeWageUpload,
  resetEmployeeUploadDetails,
  resetEmployeeAttendanceUploadDetails,
  resetEmployeeLeaveCreditUploadDetails,
  resetEmployeeLeaveAvailedUploadDetails,
  resetEmployeeWageUploadDetails,
  resetConfigUploadDetails
} from '../../../../redux/features/performerClient.slice';
import { SAMPLE_FORMAT_S3_KEY_MAPPING } from '../../../../utils/registersQue.utils';
import { InputModuleService } from '../../../../backend/inputModule';
import { downloadFormat, getInputModuleMappingDetails, resetGetConfigMappingDetails, resetDownloadFormat} from '../../../../redux/features/inputModule.slice';
import { ERROR_MESSAGES } from '../../../../utils/constants';
import { download, preventDefault, downloadFileContent } from '../../../../utils/common';
import { hasUserAccess } from '../../../../backend/auth';
import { USER_PRIVILEGES } from '../../UserManagement/Roles/RoleConfiguration';
import cloudUpload from '../../../../assets/img/icon/cloudUpload.svg';
import './performer-client.css';

const styleUploadModal = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 660,
  backgroundColor: '#FFFFFF',
  borderRadius: '8px',
  outline: 'none',
  fontFamily: 'Montserrat !important',
};

interface UploadButtonProps {
  configType: 'Employee' | 'Leave Credit' | 'Leave Availed' | 'Employee Attendance' | 'Employee Wage';
  filters: {
    company: string;
    associateCompany: string;
    establishmentType: string;
    location?: string;
  };
  selectedAdditionalFilters?: {
    location?: string;
    employer?: any;
  };
  onUploadSuccess?: () => void;
}

const UploadButton: React.FC<UploadButtonProps> = ({
  configType,
  filters,
  selectedAdditionalFilters = {},
  onUploadSuccess
}) => {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const employeeUploadDetails = useAppSelector((state) => state.performerClient.employeeUploadDetails);
  const employeeAttendanceUploadDetails = useAppSelector((state) => state.performerClient.employeeAttendanceUploadDetails);
  const employeeLeaveCreditUploadDetails = useAppSelector((state) => state.performerClient.employeeLeaveCreditUploadDetails);
  const employeeLeaveAvailedUploadDetails = useAppSelector((state) => state.performerClient.employeeLeaveAvailedUploadDetails);
  const employeeWageUploadDetails = useAppSelector((state) => state.performerClient.employeeWageUploadDetails);
  const configMappingDetails = useAppSelector((state) => state.inputModule.configMappingDetails);
  const downloadFormatDetails = useAppSelector((state) => state.inputModule.downloadFormat);

  const MAX_FILE_SIZE_MB = 25;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
  const ALLOWED_EXTENSIONS = ['.xls', '.xlsx'];
const [openUploadModal, setOpenUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState<any>(null);
  const [uploadError, setUploadError] = useState(false);
  const [errorfilepath, setErrorFilePath] = useState<any>();

  const getUploadDetails = () => {
    switch (configType) {
      case 'Employee': return employeeUploadDetails;
      case 'Employee Attendance': return employeeAttendanceUploadDetails;
      case 'Leave Credit': return employeeLeaveCreditUploadDetails;
      case 'Leave Availed': return employeeLeaveAvailedUploadDetails;
      case 'Employee Wage': return employeeWageUploadDetails;
      default: return employeeUploadDetails;
    }
  };

  const uploadDetails = getUploadDetails();

  const isCheckingMapping = configMappingDetails.status === 'loading';
  const isUploading = uploadDetails.status === 'loading';

  const onClickUpload = () => {
    if (!filters.company || !filters.associateCompany) {
      return toast.error(ERROR_MESSAGES.FILL_ALL);
    }
    const hasEmployer = Boolean(selectedAdditionalFilters.employer);
    const filterParams: any = {
      configuration_type_eq: configType,
      company_id_eq: filters.company,
      associate_company_id_eq: filters.associateCompany,
      establishment_type_eq: filters.establishmentType,
      is_assoc_cmp_config_eq: !hasEmployer,
    };
    if (filters.location) {
      filterParams.location_id_eq = filters.location;
    } else {
      filterParams.location_id_null = true;
    }
    if (hasEmployer) {
      filterParams.employer_id_eq = (selectedAdditionalFilters.employer as any).value;
    } else {
      filterParams.employer_id_null = true;
    }
    const payload: any = {
      search: '',
      filters: filterParams,
      per: 200,
      page: 1,
      sorts: ['company_id asc'],
      includeCentral: true
    };
    dispatch(getInputModuleMappingDetails(payload));
  };

  const getMappingLevelMessage = () => {
    const hasEmployer = Boolean(selectedAdditionalFilters.employer);
    const hasLocation = Boolean(filters.location);

    if (hasEmployer) {
      return 'Field mapping is not configured at the Employer level. Please check if mapping is configured at the Associate Company or Location level and select your filters accordingly.';
    }
    if (hasLocation) {
      return 'Field mapping is not configured at the Location level. Please check if mapping is configured at the Associate Company level and select your filters accordingly.';
    }
    return 'Field mapping is not configured for the selected Associate Company. Please complete the field mapping';
  };

  useEffect(() => {
    if (configMappingDetails.status === 'succeeded') {
      const items = configMappingDetails.data && configMappingDetails.data.items || [];
      if (items.length === 0) {
        toast.error(getMappingLevelMessage());
      } else {
        setOpenUploadModal(true);
      }
      dispatch(resetGetConfigMappingDetails());
    } else if (configMappingDetails.status === 'failed') {
      toast.error(ERROR_MESSAGES.DEFAULT);
      dispatch(resetGetConfigMappingDetails());
    }
  }, [configMappingDetails.status]);

  useEffect(() => {                                                                                                                                                                                         
    if (downloadFormatDetails.status === 'succeeded') {                                                                                                                                                     
      toast.success('The template will be sent on your registered email.');                                                                                                                                          
      dispatch(resetDownloadFormat());                                                                                                                                                                      
    } else if (downloadFormatDetails.status === 'failed') {                                                                                                                                                 
      toast.error('Failed to send sample format. Please try again.');                                                                                                                                       
      dispatch(resetDownloadFormat());                                                                                                                                                                      
    }                                                                                                                                                                                                       
  }, [downloadFormatDetails.status]);


  const handleDownloadFormat = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!filters.company || !filters.associateCompany) {
      return toast.error(ERROR_MESSAGES.FILL_ALL);
    }
    dispatch(downloadFormat({
      type: configType,
      associate_company_id: filters.associateCompany,
      establishment_type: filters.establishmentType,
      location_id: selectedAdditionalFilters.location || '',
      employer_id: selectedAdditionalFilters.employer && selectedAdditionalFilters.employer.value || ''
    }));
  };

  const handleDownloadSampleFormat = async (e: React.MouseEvent) => {
    e.preventDefault();                                                                                                                                                                                     
    const s3Key = SAMPLE_FORMAT_S3_KEY_MAPPING[filters.establishmentType] && SAMPLE_FORMAT_S3_KEY_MAPPING[filters.establishmentType][configType];
    if (!s3Key) return toast.error('Sample format not available for this configuration type.');                                                                                                             
    try {                                                                                                                                                                                                   
      const s3Bucket = process.env.REACT_APP_S3_BUCKET || 'mafoi-internal';                                                                                                                                 
      const inputModuleService = new InputModuleService();                                                                                                                                                  
      await inputModuleService.emailSampleFormat({      
        s3_key: s3Key,                                                                                                                                                                                      
        s3_bucket: s3Bucket,                            
        configuration_type: configType,
        establishment_type: filters.establishmentType,                                                                                                                                                                     
      });
      toast.success('The template will be sent on your registered email.');                                                                                       
    } catch (e) {                                                                                                                                                                                           
      toast.error('Failed to send sample format. Please try again.');                                                                                                                                       
    }                                                                                                                                                                                                       
  };

  const onClickSubmitUpload = () => {
    const formData = new FormData();
    const data = uploadData ? uploadData[0] : [];
    formData.append('file', data);
    formData.append('remarks', 'NA');
    formData.append('mapped', 'false');
    formData.append('configuration_type', configType);
    formData.append('company_id', filters.company);
    formData.append('associate_company_id', filters.associateCompany);
    formData.append('establishment_type', filters.establishmentType);
    const is_assoc_cmp_config_eq = !selectedAdditionalFilters.employer ? true : false;
    formData.append('is_assoc_cmp_config', is_assoc_cmp_config_eq.toString());
    if (filters.establishmentType === 'CLRA' && filters.location) {
      formData.append('location_id', filters.location);
    }
    if (selectedAdditionalFilters.employer) {
      formData.append('employer_id', selectedAdditionalFilters.employer.value);
    }

    if (configType === 'Employee') {
      dispatch(employeeUpload(formData));
    } else if (configType === 'Employee Attendance') {
      dispatch(employeeAttendanceUpload(formData));
    } else if (configType === 'Leave Credit') {
      dispatch(employeeLeaveCreditUpload(formData));
    } else if (configType === 'Leave Availed') {
      dispatch(employeeLeaveAvailedUpload(formData));
    } else if (configType === 'Employee Wage') {
      dispatch(employeeWageUpload(formData));
    }
  };

  const downloadErrors = (e: any) => {
    preventDefault(e);
    const data = errorfilepath.data;
    const blob = new Blob([data], { type: errorfilepath.headers['content-type'] });
    const URL = window.URL || window.webkitURL;
    const downloadUrl = URL.createObjectURL(blob);
    download('Errors.xlsx', downloadUrl);
  };

  const resetUploadDetails = () => {
    dispatch(resetConfigUploadDetails());
    dispatch(resetEmployeeUploadDetails());
    dispatch(resetEmployeeAttendanceUploadDetails());
    dispatch(resetEmployeeLeaveCreditUploadDetails());
    dispatch(resetEmployeeLeaveAvailedUploadDetails());
    dispatch(resetEmployeeWageUploadDetails());
    setUploadError(false);
  };

  const closeModal = () => {
    setOpenUploadModal(false);
    setUploadError(false);
    setUploadData(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        toast.error(`Invalid file format. Allowed formats: XLS, XLSX`);
        e.target.value = '';
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit`);
        e.target.value = '';
        return;
      }
      setUploadData(Array.from(e.target.files));
    }
    e.target.value = '';
  };

  const handleRemoveFile = () => {
    setUploadData(null);
  };

  const handleChooseFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const validateAndSetFile = (file: File | null | undefined) => {
    if (!file) return;
    const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error('Invalid file format. Allowed formats: XLS, XLSX');
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit`);
      return;
    }
    setUploadData([file]);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  useEffect(() => {
    if (uploadDetails.status === 'succeeded') {
      const contentType = uploadDetails.data && uploadDetails.data.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        resetUploadDetails();
        closeModal();
        toast.success('Data upload is in progress. You will receive an email once it\'s complete.');
        if (onUploadSuccess) {
          onUploadSuccess();
        }
      } else {
        setErrorFilePath(uploadDetails.data);
        setUploadError(true);
        setUploadData(null);
      }
    } else if (uploadDetails.status === 'failed') {
      toast.error(ERROR_MESSAGES.DEFAULT);
      resetUploadDetails();
      setUploadData(null);
    }
  }, [uploadDetails.status]);

  if (!hasUserAccess(USER_PRIVILEGES.UPLOAD_PERFORMER_CLIENT_DATA)) {
    return null;
  }

  return (
    <>
      <button onClick={onClickUpload} className="button-primary" disabled={isCheckingMapping}>
        {'Upload'}
      </button>

      <Modal open={openUploadModal} onClose={closeModal}>
        <Box sx={styleUploadModal}>
          {/* Header */}
          <Box sx={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontFamily: 'Montserrat !important' }}>
            <Box>
              <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#1E1E1E', lineHeight: '28px', fontFamily: 'Montserrat !important' }}>
                Upload Employee Data
              </Typography>
            </Box>
            <IconButton onClick={closeModal} sx={{ fontSize: '20px', padding: '0px' }}>
              <IoMdClose />
            </IconButton>
          </Box>

          <Divider sx={{ margin: '0px 16px' }} />

          {/* Body */}
          <Box sx={{ padding: '16px' }}>
            {uploadError && (
              <Alert variant="danger" className="mb-3">
                There are few errors identified in the file uploaded. Correct the errors and upload again.{' '}
                <a href="/" onClick={downloadErrors}>Click here</a> to download the errors.
              </Alert>
            )}

            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#1E1E1E', marginBottom: '8px', fontFamily: 'Montserrat !important' }}>
              Upload files
            </Typography>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx, .xls"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />

            {/* Upload box — shown when no file selected */}
            {!uploadData && (
              <div
                className="performer-upload-box p-4 text-center"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDrop={handleDrop}
              >
                <div className="mb-2">
                  <img src={cloudUpload} alt="upload" />
                </div>
                <p className="mb-2" style={{ color: '#757575', fontSize: '14px', fontFamily: 'Montserrat !important' }}>
                  Drag and drop files here or click to browse
                </p>
                <button
                  type="button"
                  className="performer-choose-file-btn mb-2"
                  onClick={handleChooseFile}
                >
                  Choose File
                </button>
                <div>
                  <small style={{ color: '#b2b2b2', fontSize: '12px' }}>
                    Supported formats(Upto 25 Mb): XLS, XLSX
                  </small>
                </div>
              </div>
            )}

            {/* File selected — shown when file is selected */}
            {uploadData && (
              <div className="performer-upload-successful position-relative">
                <div
                  className="performer-upload-close position-absolute"
                  onClick={handleRemoveFile}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M10.0002 5.99992L6.00016 9.99992M6.00016 5.99992L10.0002 9.99992M14.6668 7.99992C14.6668 11.6818 11.6821 14.6666 8.00016 14.6666C4.31826 14.6666 1.3335 11.6818 1.3335 7.99992C1.3335 4.31802 4.31826 1.33325 8.00016 1.33325C11.6821 1.33325 14.6668 4.31802 14.6668 7.99992Z"
                      stroke="#757575"
                      strokeWidth="1.33333"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="p-3 d-flex align-items-center">
                  <p className="m-0" style={{ fontSize: '14px', color: '#1E1E1E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '90%' }}>
                    {uploadData[0].name}
                  </p>
                </div>
              </div>
            )}

            {/* Download Sample Format */}
            <div style={{ marginTop: '9px' }}>
              <a
                href="/"
                onClick={handleDownloadFormat}
                style={{ fontSize: '12px', fontWeight: 500, color: '#2965AD', display: 'inline-flex', alignItems: 'center', gap: '6px', textDecoration: 'underline', cursor: 'pointer' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M2.66669 11.3333V12.6666C2.66669 13.0202 2.80716 13.3594 3.05721 13.6094C3.30726 13.8595 3.64640 14 4.00002 14H12C12.3536 14 12.6928 13.8595 12.9428 13.6094C13.1929 13.3594 13.3334 13.0202 13.3334 12.6666V11.3333M10.6667 8L8.00002 10.6666M8.00002 10.6666L5.33335 8M8.00002 10.6666V2.66663" stroke="#2965AD" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span style={{ textDecoration: 'underline', textUnderlineOffset: '2px' }}>Download field mapping headers</span>
              </a>
            </div>
            {SAMPLE_FORMAT_S3_KEY_MAPPING[filters.establishmentType] && SAMPLE_FORMAT_S3_KEY_MAPPING[filters.establishmentType][configType] && (
              <div style={{ marginTop: '6px' }}>                                                                                                                                                                      
                <a
                  href="/"                                                                                                                                                                                            
                  onClick={handleDownloadSampleFormat}            
                  style={{ fontSize: '12px', fontWeight: 500, color: '#2965AD', display: 'inline-flex', alignItems: 'center', gap: '6px', textDecoration: 'underline', cursor: 'pointer' }}
                >                                                                                                                                                                                                     
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M2.66669 11.3333V12.6666C2.66669 13.0202 2.80716 13.3594 3.05721 13.6094C3.30726 13.8595 3.64640 14 4.00002 14H12C12.3536 14 12.6928 13.8595 12.9428 13.6094C13.1929 13.3594 13.3334     
            13.0202 13.3334 12.6666V11.3333M10.6667 8L8.00002 10.6666M8.00002 10.6666L5.33335 8M8.00002 10.6666V2.66663" stroke="#2965AD" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>        
                  </svg>                                                                                                                                                                                              
                  <span style={{ textDecoration: 'underline', textUnderlineOffset: '2px' }}>Download sample format and instructions</span>                                                                                             
                </a>                                                                                                                                                                                                  
              </div>
            )}
          </Box>

          <Divider sx={{ margin: '0px 16px' }} />

          {/* Footer */}
          <Box sx={{ padding: '16px', display: 'flex', gap: '24px' }}>
            <button
              className="button-secondary w-50"
              onClick={closeModal}
            >
              Cancel
            </button>
            <button
              className="button-primary w-50"
              disabled={!uploadData || isUploading}
              onClick={onClickSubmitUpload}
            >
              {isUploading ? 'Uploading...' : 'Upload'}
            </button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default UploadButton;
